import React, { useEffect, useRef, useState } from "react";
import Swal from "sweetalert2";
import { SETTING_OPTIONS, makeId, buildTeams, defaultCheckpointsForSetting, labelFor, TEAM_COUNT_OPTIONS, TEAM_COUNT_MIN, TEAM_COUNT_MAX } from "../constants";
import CountField from "./CountField";

function SettingLocations({ hunt, updateHunt, themeMeta }) {
  const [form, setForm] = useState({ name: "", location: "" });
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState({ name: "", location: "" });

  const checkpoints = hunt.checkpoints || [];
  // No separate "use suggested checkpoints" toggle — that was redundant with the Setting picker
  // itself, which already has a "Custom" option for exactly this case. Any other Setting shows
  // its own suggested checkpoints; picking Custom means starting blank and adding your own.
  const useSuggested = hunt.setting !== "custom";

  // While on a non-Custom Setting, the list mirrors it — switching settings regenerates it live.
  // This must NOT re-fire just because this step remounted (e.g. the admin navigated to another
  // step and back) — otherwise any custom checkpoint added earlier this session gets silently
  // wiped back to the suggested 6 the moment they leave and return to this step. `hydratedRef`
  // tracks whether this specific mount has already made its first pass, so only a genuine
  // setting change (never a plain remount with checkpoints already in place) regenerates the list.
  const hydratedRef = useRef(false);
  const prevSettingRef = useRef(hunt.setting);
  useEffect(() => {
    if (!themeMeta) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      prevSettingRef.current = hunt.setting;
      if (useSuggested && checkpoints.length === 0) {
        updateHunt({ checkpoints: defaultCheckpointsForSetting(hunt.setting) });
      }
      return;
    }
    const settingChanged = prevSettingRef.current !== hunt.setting;
    prevSettingRef.current = hunt.setting;
    if (useSuggested && settingChanged) {
      updateHunt({ checkpoints: defaultCheckpointsForSetting(hunt.setting) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [themeMeta, useSuggested, hunt.setting]);

  const addCheckpoint = () => {
    if (!form.name.trim()) {
      Swal.fire("Name required", "Give this checkpoint a name.", "warning");
      return;
    }
    updateHunt({
      checkpoints: [...checkpoints, { id: makeId("cp"), name: form.name.trim(), location: form.location.trim() }],
    });
    setForm({ name: "", location: "" });
  };

  const startEdit = (cp) => {
    setEditingId(cp.id);
    setEditDraft({ name: cp.name, location: cp.location });
  };
  const saveEdit = (id) => {
    updateHunt({
      checkpoints: checkpoints.map((c) => (c.id === id ? { ...c, ...editDraft } : c)),
    });
    setEditingId(null);
  };
  const removeCheckpoint = (id) => {
    updateHunt({ checkpoints: checkpoints.filter((c) => c.id !== id) });
    if (editingId === id) setEditingId(null);
  };

  const setNumTeams = (n) => {
    updateHunt({ numTeams: n, teams: buildTeams(n, hunt.teams || []) });
  };

  const teamCount = hunt.numTeams || 0;
  const roundCount = hunt.numRounds || 0;
  // Checkpoints have to cover both: enough that no two teams share one in the same round (>=
  // teamCount), AND enough that one team's own route never has to revisit a checkpoint it already
  // stood at earlier (>= roundCount) — whichever of the two demands more is the real floor.
  const requiredCheckpoints = Math.max(teamCount, roundCount);
  const short = checkpoints.length < requiredCheckpoints;
  const need = requiredCheckpoints - checkpoints.length;
  const shortReason =
    teamCount >= roundCount ? "give every team its own checkpoint in every round" : "let every team visit a different checkpoint each round";

  return (
    <div className="studio-step-page" data-tour="studio-step-locations">
      <h2 className="studio-h2">Setting &amp; locations</h2>
      <p className="studio-sub">Pick the venue type, then add every checkpoint teams can be sent to.</p>

      <div className="studio-field">
        <label>Setting</label>
        <div className="studio-pill-row">
          {SETTING_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={"studio-pill" + (hunt.setting === opt.value ? " studio-pill--active" : "")}
              onClick={() => updateHunt({ setting: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <div className="studio-hint">Setting decides which checkpoint icons are offered. It does not change the puzzles.</div>
      </div>

      <CountField
        title="Number of teams"
        description="Choose how many teams will play."
        value={hunt.numTeams}
        onChange={setNumTeams}
        options={TEAM_COUNT_OPTIONS}
        min={TEAM_COUNT_MIN}
        max={TEAM_COUNT_MAX}
        unitLabel="teams"
      />

      <div className="studio-row-between" style={{ marginTop: 24 }}>
        <h3 className="studio-h3">Checkpoints ({checkpoints.length})</h3>
      </div>
      {useSuggested ? (
        <div className="studio-hint" style={{ marginTop: -8, marginBottom: 14 }}>
          Showing the 6 suggested checkpoints for {labelFor(SETTING_OPTIONS, hunt.setting)} — edit, remove, or add
          your own below. Switching setting regenerates this list back to its 6 suggestions — pick "Custom" above
          if you don't want your own additions wiped out later.
        </div>
      ) : (
        <div className="studio-hint" style={{ marginTop: -8, marginBottom: 14 }}>
          Custom setting — add every checkpoint yourself below; nothing is suggested or regenerated for you.
        </div>
      )}

      <div className="studio-checkpoint-grid">
        {checkpoints.map((cp) => (
          <div className="studio-checkpoint-card" key={cp.id}>
            {editingId === cp.id ? (
              <div className="studio-checkpoint-edit">
                <input
                  className="studio-input"
                  value={editDraft.name}
                  onChange={(e) => setEditDraft((d) => ({ ...d, name: e.target.value }))}
                  placeholder="Name"
                />
                <input
                  className="studio-input"
                  value={editDraft.location}
                  onChange={(e) => setEditDraft((d) => ({ ...d, location: e.target.value }))}
                  placeholder="Location"
                />
                <div className="studio-checkpoint-edit-actions">
                  <button className="studio-link-btn" onClick={() => saveEdit(cp.id)}>Save</button>
                  <button className="studio-link-btn studio-link-btn--danger" onClick={() => removeCheckpoint(cp.id)}>Remove</button>
                </div>
              </div>
            ) : (
              <>
                <div className="studio-checkpoint-dot" />
                <div className="studio-checkpoint-info">
                  <div className="studio-checkpoint-name">{cp.name}</div>
                  <div className="studio-checkpoint-location">{cp.location}</div>
                </div>
                <button className="studio-link-btn" onClick={() => startEdit(cp)}>Edit</button>
              </>
            )}
          </div>
        ))}

        <div className="studio-checkpoint-card studio-checkpoint-card--add">
          <input
            className="studio-input"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Checkpoint name"
          />
          <input
            className="studio-input"
            value={form.location}
            onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
            placeholder="Location detail"
          />
          <button className="studio-btn studio-btn--primary" onClick={addCheckpoint}>
            Add checkpoint
          </button>
        </div>
      </div>

      {requiredCheckpoints > 0 && (
        <div className={"studio-banner" + (short ? "" : " studio-banner--ok")}>
          <span className="studio-dot" />
          <div>
            <div className="studio-banner-title">
              You have {checkpoints.length} checkpoints, {teamCount} teams, and {roundCount} rounds
            </div>
            {short && (
              <div className="studio-banner-desc">
                Add at least {need} more to {shortReason}.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default SettingLocations;
